/**
 * Physical placement of the multi-line chart's Y axes (djin-cloud#829).
 *
 * The logical ids (`left` / `right` / `right2`) name a UNIT GROUP, not a screen
 * position. HistorianContext assigns them by unit (kW -> `right`, Hz ->
 * `right2`), and scale settings and persisted selections key on them, so they
 * are left alone here. Drawing them literally, though, meant a kW + Hz chart
 * put both axes on the right, 64 px apart, while the left edge held an axis
 * no series used. Their crosshair labels drew on top of each other (reported
 * on Bench-01 METER-001 as `-0.00000(` over `60.0140`).
 *
 * The rule: occupied groups take physical slots in logical order.
 *   1st -> left edge
 *   2nd -> right edge
 *   3rd -> right edge, pushed out by OUTER_RIGHT_AXIS_OFFSET
 *
 * With one or two groups no two axes share an edge, so their labels cannot
 * overlap, however long they get. With three groups, one edge has to carry two
 * axes. That case is bounded by a width budget rather than guaranteed; see
 * OUTER_RIGHT_AXIS_OFFSET.
 *
 * Kept in its own module, not the wrapper, because the historian's axis chips
 * must name the same edge the wrapper draws on. Both read this one function.
 */

export type YAxisId = 'left' | 'right' | 'right2';

export type YAxisSide = 'left' | 'right';

export interface YAxisPlacement {
  /** The logical unit group drawn on this axis. */
  id: YAxisId;
  /** Index into the ECharts `yAxis` array: what a series' `yAxisIndex` names. */
  index: number;
  side: YAxisSide;
  /** Pixels the axis is pushed outward from the grid edge. */
  offset: number;
}

/** The order in which occupied groups claim a physical slot. */
const SLOT_ORDER: readonly YAxisId[] = ['left', 'right', 'right2'];

/**
 * Outward offset for the third axis, which shares the right edge.
 *
 * The inner right axis draws two kinds of label outward from the grid edge:
 *   - tick labels, from +8 px (ECharts' axisLabel margin);
 *   - the crosshair label, a box from +3 px (axisPointer label margin) that is
 *     the text plus 7 px of padding on each side, printed at the tick
 *     precision + 2 (so "-1,234.57" on a kW axis with a 1000 kW step).
 * The outer axis's crosshair box starts at offset + 3. Taking a glyph as
 * 7.2 px at 12 px (JetBrains Mono's advance; Inter's digits are close, but
 * that part is an approximation), 96 px keeps the two crosshair boxes apart
 * for inner labels up to 11 characters ("-123,456.78") and tick labels up
 * to 12. A longer label on the inner axis can still reach the outer one.
 * The previous 64 px fit 6 characters, which a single flat-zero kW crosshair
 * label (15 characters) overran.
 */
export const OUTER_RIGHT_AXIS_OFFSET = 96;

/**
 * Place the Y axes needed by a set of series axis ids.
 *
 * An absent id means `left`, matching the wrapper's series default. With no
 * series at all, a single left axis is still returned, because the grid needs
 * a Y axis to exist.
 */
export function layoutYAxes(ids: Iterable<YAxisId | undefined>): YAxisPlacement[] {
  const used = new Set<YAxisId>();
  for (const id of ids) used.add(id ?? 'left');

  const occupied = SLOT_ORDER.filter((id) => used.has(id));
  if (occupied.length === 0) occupied.push('left');

  return occupied.map((id, index) => ({
    id,
    index,
    side: index === 0 ? 'left' : 'right',
    offset: index === 2 ? OUTER_RIGHT_AXIS_OFFSET : 0,
  }));
}
